import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import Loader from "../compoment/ui/Loader";
import SigninModal from "../modal/SigninModal";
import SignupModal from "../modal/SignupModal";
import ForgotPasswordModal from "../modal/ForgotPasswordModal";
import VerificationModal from "../modal/VerificationModal";
import ResetModal from "../modal/ResetModal";
import SuccessModal from "../modal/SuccessModal";
import EmailVerificationModal from "../modal/EmailVerificationModal";
import LoaderSaveModal from "../modal/LoaderSaveModal";
import SelectHobbyConfirmModal from "../modal/SelectHobbyConfirmModal";

import type { AiSelfieArt } from "../services/aiSelfieArtService";
import {
    effectiveStyleSlugForAiSelfieArt,
    isCaricatureOrCartoonArt,
    isPrintYourOwnFlow,
    readSelectedAiSelfieArtFromStorage,
    resolveSelectedAiSelfieArtFavouriteType,
    resolveHobbyCaricatureRouteSlug,
    shouldUseHobbyCaricatureFlowForStoredAiSelfieArt,
    shouldUsePetCaricatureBackgroundFlow,
} from "../services/stylePromptResolver";
import { resolveArtDisplayName } from "../utils/artDisplayName";
import { AuthUser } from "../modal/auth/types";
import { authService } from "../modal/auth/authService";
import {
    checkFavouriteImageAlreadySaved,
    createFavourite,
    persistFavouriteArtSavedLocally,
} from "../services/favouritesService";
import {
    clearPreviousProcessedImageUrl,
    setPreviousProcessedImageUrl,
    setProcessedImageUrl,
} from "../redux/slices/uploadFlowSlice";
import {
    finalizeOrderWithGeneratedArt,
    persistLatestChosenArtUrl,
} from "../utils/finalizeOrderWithGeneratedArt";
import { clearPendingPaymentOrder } from "../utils/pendingPaymentOrder";
import {
    clearTransformationTryAgainUsed,
    persistTransformationTryAgainUsed,
    readTransformationTryAgainUsed,
    resolveStationRouteSlug,
} from "../utils/stationFlowStorage";
import { toast } from "react-toastify";
import StationThemeLayout from "../layouts/StationThemeLayout";
import SignOutConfirmModal from "../modal/SignOutConfirmModal";
import { resolveAuthAccessToken } from "../utils/authSession";

interface TransformationResultScreenProps {
    styleSlug?: string;
}

type TransformationResultPhase = "save-prompt" | "save-artwork-prompt" | "claim-instructions" | "compare-prompt";

const TransformationResultScreen: React.FC<TransformationResultScreenProps> = ({ styleSlug: styleSlugProp }) => {
    const { slug: slugParam } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const { processedImageUrl, previousProcessedImageUrl }: any = useSelector(
        (s: RootState) => s.uploadFlow
    );
    const [signinModal, setSigninModal] = useState<boolean>(false);
    const [signupModal, setSignupModal] = useState<boolean>(false);
    const [emailVerificationModal, setEmailVerificationModal] = useState<boolean>(false);
    const [saveModal, setSaveModal] = useState<boolean>(false);
    const [forgotPasswordModal, setForgotPasswordModal] = useState<boolean>(false);
    const [verificationModal, setVerificationModal] = useState<boolean>(false);
    const [resetModal, setResetModal] = useState<boolean>(false);
    const [successModal, setSuccessModal] = useState<boolean>(false);

    const [isSavingToFavourites, setIsSavingToFavourites] = useState(false);
    const [resultPhase, setResultPhase] = useState<TransformationResultPhase>("save-prompt");
    const [tryAgainUsed, setTryAgainUsed] = useState(() => readTransformationTryAgainUsed());
    const [selectedCompareUrl, setSelectedCompareUrl] = useState<string | null>(null);
    const [pendingCompareSelection, setPendingCompareSelection] = useState<{
        label: string;
        url: string;
    } | null>(null);
    const [showCompareConfirmModal, setShowCompareConfirmModal] = useState(false);
    const [showBackConfirmModal, setShowBackConfirmModal] = useState(false);
    const [resultImageLoaded, setResultImageLoaded] = useState(false);
    const [compareImagesLoaded, setCompareImagesLoaded] = useState<Record<string, boolean>>({});
    const resultImageRef = useRef<HTMLImageElement>(null);
    const [isUpdatingOrder, setIsUpdatingOrder] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const [showFavouritePrompt, setShowFavouritePrompt] = useState(false);

    // After 10s hide person image and show action buttons
    useEffect(() => {
        const timer = setTimeout(() => setShowActions(true), 5000);
        return () => clearTimeout(timer);
    }, []);

    // Selected station: prefer navigation state, then selectedStation, then stationData
    const stationData = useMemo(() => {
        const selectedRaw = localStorage.getItem("selectedStation");
        const fromSelected = selectedRaw ? JSON.parse(selectedRaw) : null;
        const fromStorage = JSON.parse(localStorage.getItem("stationData") || "{}");
        return fromSelected || fromStorage || {};
    }, [location.state]);

    const selectedAiSelfieArt = useMemo((): Partial<AiSelfieArt> | null => {
        try {
            const raw = localStorage.getItem("selectedAiSelfieArt");
            if (!raw) return null;
            return JSON.parse(raw) as Partial<AiSelfieArt>;
        } catch {
            return null;
        }
    }, [slugParam]);

    const styleSlug = useMemo(
        () =>
            styleSlugProp ||
            resolveStationRouteSlug(stationData as any, slugParam, selectedAiSelfieArt) ||
            "pencil-sketch",
        [styleSlugProp, stationData, slugParam, selectedAiSelfieArt]
    );

    const layoutSlug = useMemo(
        () => effectiveStyleSlugForAiSelfieArt(selectedAiSelfieArt, styleSlug) ?? styleSlug,
        [selectedAiSelfieArt, styleSlug]
    );

    const artDisplayName = useMemo(
        () => resolveArtDisplayName({ selectedArt: selectedAiSelfieArt, stationData: stationData as { name?: string } }),
        [selectedAiSelfieArt, stationData]
    );

    const isArtLandscape = useMemo(
        () =>
            isCaricatureOrCartoonArt(
                selectedAiSelfieArt?.name,
                artDisplayName,
                layoutSlug,
                (stationData as { type?: string; name?: string })?.type,
                (stationData as { type?: string; name?: string })?.name
            ),
        [selectedAiSelfieArt?.name, artDisplayName, layoutSlug, stationData]
    );

    const resultHeadings = useMemo<Record<TransformationResultPhase, string>>(
        () => ({
            "save-prompt": `Do you like the result?`,
            "save-artwork-prompt": `Would you like to save your ${artDisplayName} artwork?`,
            "claim-instructions": `Please claim your ${artDisplayName} at the printing station`,
            "compare-prompt": "Choose which one you like",
        }),
        [artDisplayName]
    );

    const showCompare =
        Boolean(
            previousProcessedImageUrl &&
            processedImageUrl &&
            previousProcessedImageUrl !== processedImageUrl
        );

    const showActionButtons = resultPhase === "save-prompt" || resultPhase === "save-artwork-prompt";
    const showTryAgain = resultPhase === "save-prompt" && !tryAgainUsed;
    const isComparePhase = resultPhase === "compare-prompt";
    const isSaveArtworkPhase = resultPhase === "save-artwork-prompt";

    useEffect(() => {
        if (showCompare && resultPhase === "save-prompt") {
            setResultPhase("compare-prompt");
        }
    }, [showCompare, resultPhase]);

    useEffect(() => {
        if (!isComparePhase) return;
        setSelectedCompareUrl(null);
        setPendingCompareSelection(null);
        setShowCompareConfirmModal(false);
        setCompareImagesLoaded({});
    }, [isComparePhase, processedImageUrl, previousProcessedImageUrl]);

    const getFallbackImage = () => {
        if (layoutSlug === "caricature" || layoutSlug === "wynwood-caricature") {
            return require("../assets/img/caricature/imag-cari-orignal.png");
        }
        if (layoutSlug === "cartoon" || layoutSlug === "90-cartoon") {
            return require("../assets/img/cartoon/result-1.png");
        }
        if (layoutSlug === "wynwood" || layoutSlug === "graffiti-monochrome" || layoutSlug === "pop-graffiti" || layoutSlug === "neon-surreal-graffiti") {
            return require("../assets/img/wynwood-walls/result-1.png");
        }
        if (
            layoutSlug === "famous-painting" ||
            layoutSlug === "andy-warhol-marilyn-monroe-style" ||
            layoutSlug === "lichtenstein" ||
            layoutSlug === "klimt-style" ||
            layoutSlug === "botanical-surreal"
        ) {
            return require("../assets/img/famous-painting/result-1.png");
        }
        return require("../assets/img/pencil-sketch/sketch-image.png");
    };
    const fallbackImage = getFallbackImage();
    const resultImage = processedImageUrl || fallbackImage;

    const compareResults = useMemo(
        () =>
            [previousProcessedImageUrl, processedImageUrl].filter(
                (url): url is string => Boolean(url)
            ),
        [processedImageUrl, previousProcessedImageUrl]
    );

    useEffect(() => {
        if (!isComparePhase) return;

        const urls = compareResults;
        if (!urls.length) return;

        const cancelled = { current: false };

        setCompareImagesLoaded(
            urls.reduce<Record<string, boolean>>((acc, url) => {
                acc[url] = false;
                return acc;
            }, {})
        );

        urls.forEach((url) => {
            const img = new Image();
            const markLoaded = () => {
                if (cancelled.current) return;
                window.requestAnimationFrame(() => {
                    if (!cancelled.current) {
                        setCompareImagesLoaded((prev) => ({ ...prev, [url]: true }));
                    }
                });
            };

            img.onload = markLoaded;
            img.onerror = markLoaded;
            img.src = url;

            if (img.complete) {
                markLoaded();
            }
        });

        return () => {
            cancelled.current = true;
        };
    }, [isComparePhase, compareResults]);

    useEffect(() => {
        if (!resultImage || isComparePhase) return;

        let cancelled = false;
        setResultImageLoaded(false);

        const img = new Image();
        img.onload = () => {
            if (!cancelled) {
                window.requestAnimationFrame(() => {
                    if (!cancelled) setResultImageLoaded(true);
                });
            }
        };
        img.onerror = () => {
            if (!cancelled) setResultImageLoaded(true);
        };
        img.src = resultImage;

        if (img.complete) {
            window.requestAnimationFrame(() => {
                if (!cancelled) setResultImageLoaded(true);
            });
        }

        return () => {
            cancelled = true;
        };
    }, [resultImage, isComparePhase]);

    const minLoaderDurationMs = 2500;

    const endArtOrderSession = () => {
        clearPendingPaymentOrder();
        clearTransformationTryAgainUsed();
    };

    /**
     * Pet caricature only: background selection before save/print.
     * All other stations keep the previous print-frame-selection exit.
     */
    const navigateAfterResult = (
        artUrl?: string | null,
        options?: { includeState?: boolean }
    ) => {
        const snapshot = (artUrl || getChosenArtUrl() || processedImageUrl || "").trim();
        const usePetBackground = shouldUsePetCaricatureBackgroundFlow(
            selectedAiSelfieArt,
            stationData as { type?: string; name?: string }
        );

        if (usePetBackground) {
            navigate(`/${styleSlug}/select-art-background`, {
                state: {
                    stationData,
                    snapshot: snapshot || undefined,
                    selectedArt: selectedAiSelfieArt,
                    artUrl: snapshot || undefined,
                },
            });
            return;
        }

        // Non-pet: preserve prior navigation shape (state only when callers opted in).
        if (options?.includeState) {
            navigate(`/${styleSlug}/print-frame-selection`, {
                state: {
                    stationData,
                    snapshot: snapshot || undefined,
                    selectedArt: selectedAiSelfieArt,
                },
            });
            return;
        }
        navigate(`/${styleSlug}/print-frame-selection`);
    };

    const getChosenArtUrl = (): string | null => {
        const fromProcessed = processedImageUrl?.trim();
        if (fromProcessed) return fromProcessed;

        const fromCompare = selectedCompareUrl?.trim();
        if (fromCompare) return fromCompare;

        return null;
    };

    const attachArtToPaidOrder = async (artUrl: string | null | undefined): Promise<boolean> => {
        const trimmed = artUrl?.trim();
        if (!trimmed) return true;

        setIsUpdatingOrder(true);
        try {
            const result = await finalizeOrderWithGeneratedArt(trimmed, { retainOrder: true });
            if (!result.success && !result.skipped) {
                alert(result.error || "Failed to attach your artwork to the order. Please try again.");
                return false;
            }
            return true;
        } finally {
            setIsUpdatingOrder(false);
        }
    };

    /** Post-payment: PATCH /product/order/{orderId}/with-payment when user accepts the result. */
    const handlePrintNow = async () => {
        const artUrl = getChosenArtUrl();
        if (!artUrl) {
            toast.error("Generated artwork is missing. Please try again.");
            return;
        }

        const attached = await attachArtToPaidOrder(artUrl);
        if (!attached) return;

        if (artUrl) {
            persistLatestChosenArtUrl(artUrl);
        }

        setShowFavouritePrompt(true);
    };

    const handleSaveArtworkYes = () => {
        setSigninModal(true);
    };

    const handleAuthSuccess = async (user: AuthUser) => {
        const artUrl = getChosenArtUrl();
        if (artUrl) {
            persistLatestChosenArtUrl(artUrl);
        }

        setSigninModal(false);
        setSignupModal(false);
        setEmailVerificationModal(false);
        setForgotPasswordModal(false);
        setVerificationModal(false);
        setResetModal(false);
        setSuccessModal(false);
        setSaveModal(false);
        setSuccess(false);

        setLoading(true);
        const minimumDelay = new Promise<void>((resolve) => setTimeout(resolve, minLoaderDurationMs));
        try {
            const favouriteType = resolveSelectedAiSelfieArtFavouriteType();
            const favouriteCheck = await checkFavouriteImageAlreadySaved({
                userId: user.id,
                imageUrl: artUrl,
                accessToken: resolveAuthAccessToken(user),
            });
            await Promise.all([
                minimumDelay,
                favouriteCheck.shouldCreate
                    ? createFavourite({
                        userId: user.id,
                        imageUrl: artUrl || undefined,
                        favouriteType,
                        type: favouriteType,
                    }).then((result) => {
                        if (result.success) {
                            persistFavouriteArtSavedLocally(artUrl);
                        }
                        return result;
                    })
                    : Promise.resolve(),
            ]);
            if (favouriteCheck.alreadySaved) {
                persistFavouriteArtSavedLocally(artUrl);
            }
        } finally {
            setLoading(false);
            endArtOrderSession();
            navigateAfterResult(artUrl);
        }
    };

    const handleGoogleAuth = async () => {
        await authService.googleAuth();
    };

    const handleSaveArtworkNo = async () => {
        const artUrl = getChosenArtUrl();
        const attached = await attachArtToPaidOrder(artUrl);
        if (!attached) return;

        if (artUrl) {
            persistLatestChosenArtUrl(artUrl);
        }
        endArtOrderSession();
        navigateAfterResult(artUrl);
    };

    /** Skip gallery-save prompt and go to print-frame-selection (merch). */
    const goToMerchSkippingGallery = async (artUrl?: string | null) => {
        const url = (artUrl || getChosenArtUrl() || "").trim() || null;
        const attached = await attachArtToPaidOrder(url);
        if (!attached) return;

        if (url) {
            persistLatestChosenArtUrl(url);
        }
        endArtOrderSession();
        navigateAfterResult(url);
    };

    const handleNextAndExit = async (_action: "next" | "exit") => {
        setSigninModal(true);
    };

    const handleCompareSelect = (url: string) => {
        if (!url) return;
        persistLatestChosenArtUrl(url);
        dispatch(setProcessedImageUrl(url));
        dispatch(clearPreviousProcessedImageUrl());
        setSelectedCompareUrl(null);
        persistTransformationTryAgainUsed(true);
        setTryAgainUsed(true);
        // After "Generate one more" pick → merch (same as Yes on like-result).
        void goToMerchSkippingGallery(url);
    };

    const handleCompareCardSelect = (url: string) => {
        if (!url) return;
        setSelectedCompareUrl(url);
        setPendingCompareSelection({ label: artDisplayName, url });
        setShowCompareConfirmModal(true);
    };

    const handleCompareConfirmNo = () => {
        setShowCompareConfirmModal(false);
        setPendingCompareSelection(null);
        setSelectedCompareUrl(null);
    };

    const handleCompareConfirmYes = () => {
        if (pendingCompareSelection?.url) {
            handleCompareSelect(pendingCompareSelection.url);
        }
        setShowCompareConfirmModal(false);
        setPendingCompareSelection(null);
    };

    const handleCompareYes = () => {
        handleCompareSelect(selectedCompareUrl ?? processedImageUrl ?? "");
    };

    const handleYes = async () => {
        if (isComparePhase) {
            handleCompareYes();
            return;
        }

        if (resultPhase === "save-prompt") {
            const artUrl = getChosenArtUrl();
            if (artUrl) {
                persistLatestChosenArtUrl(artUrl);
            }
            // "Do you like the result?" Yes → merch (skip gallery save).
            await goToMerchSkippingGallery(artUrl);
            return;
        }

        if (isSaveArtworkPhase) {
            handleSaveArtworkYes();
        }
    };

    const handleNo = async () => {
        if (isSaveArtworkPhase) {
            await handleSaveArtworkNo();
            return;
        }

        const artUrl = getChosenArtUrl();
        const attached = await attachArtToPaidOrder(artUrl);
        if (!attached) return;

        if (artUrl) {
            persistLatestChosenArtUrl(artUrl);
        }
        setResultPhase("claim-instructions");
    };

    useEffect(() => {
        if (resultPhase !== "claim-instructions") return;

        const timer = setTimeout(() => {
            endArtOrderSession();
            navigateAfterResult(getChosenArtUrl() || processedImageUrl, {
                includeState: true,
            });
        }, 3000);

        return () => clearTimeout(timer);
    }, [resultPhase, navigate, stationData, processedImageUrl, selectedAiSelfieArt]);

    const handleTryAgain = () => {
        if (processedImageUrl) {
            dispatch(setPreviousProcessedImageUrl(processedImageUrl));
        }

        persistTransformationTryAgainUsed(true);
        setTryAgainUsed(true);

        if (shouldUseHobbyCaricatureFlowForStoredAiSelfieArt()) {
            navigate(`/${resolveHobbyCaricatureRouteSlug(styleSlug)}/select-media-library`);
            return;
        }
        const artSlug = resolveStationRouteSlug(
            stationData as any,
            slugParam,
            selectedAiSelfieArt || readSelectedAiSelfieArtFromStorage()
        );
        if (!artSlug) {
            navigate("/");
            return;
        }

        // Snap-print / Print Your Own: pick take/upload again instead of re-running generate.
        if (
            isPrintYourOwnFlow({
                slug: artSlug,
                stationType: (stationData as { type?: string })?.type,
                artName: selectedAiSelfieArt?.name,
            })
        ) {
            navigate(`/${artSlug}/choose-option`, {
                state: {
                    stationData,
                    selectedArt: selectedAiSelfieArt,
                },
            });
            return;
        }

        navigate(`/${artSlug}/image-transformation`, {
            state: {
                stationData,
                skipPrompt: true,
            },
        });
    };


    useEffect(() => {
        if (!saveModal) return
        let showLoaderTimer: NodeJS.Timeout;
        let loadingTimer: NodeJS.Timeout;
        let successTimer: NodeJS.Timeout;
        let navigateTimer: NodeJS.Timeout;

        // 1️⃣ Show loader after 5 seconds
        setEmailVerificationModal(false)
        showLoaderTimer = setTimeout(() => {
            setLoading(true);
            loadingTimer = setTimeout(() => {
                setLoading(false);
                setSuccess(true);
                successTimer = setTimeout(() => {
                    setSuccess(false);
                    navigateTimer = setTimeout(() => {
                        endArtOrderSession();
                        navigateAfterResult();
                    }, 300);

                }, 1200);

            }, 4000);

        }, 5000);

        return () => {
            clearTimeout(showLoaderTimer);
            clearTimeout(loadingTimer);
            clearTimeout(successTimer);
            clearTimeout(navigateTimer);
        };
    }, [navigate, location.state, saveModal]);
    return (
        <>
            {isSavingToFavourites && <Loader />}
            <StationThemeLayout
                className={`transformationResultSection${isComparePhase ? " is-compare-mode" : ""}`}
                station={stationData as { backgroundImageUrl?: string; type?: string; name?: string }}
                showStationLogo
                onBack={() => setShowBackConfirmModal(true)}
            >
                <div className="transformationResultInner">
                    <div className="transformationResultContent mt-5 py-0">
                        {isComparePhase && (
                            <p className="transformationResultHeading transformationResultHeading--compare">
                                {resultHeadings[resultPhase]}
                            </p>
                        )}

                        <div
                            className={`transformationResultCompareBlock${isComparePhase ? " is-compare" : ""}${isArtLandscape ? " isArtLandscape" : " isArtSquare"}`}
                        >
                            {isComparePhase ? (
                                <>
                                    <div className="transformationResultCompareRow">
                                        {compareResults.map((url) => {
                                            const isLoaded = compareImagesLoaded[url];
                                            const isSelected = selectedCompareUrl === url;

                                            return (
                                                <div
                                                    key={url}
                                                    className="transformationResultCompareItem"
                                                >
                                                    <button
                                                        type="button"
                                                        className={`transformationResultArtCard transformationResultCompareCard${isLoaded ? " is-loaded" : " is-shimmer"}${isSelected ? " is-selected" : ""}`}
                                                        onClick={() => handleCompareCardSelect(url)}
                                                        aria-label={`Select ${artDisplayName} result`}
                                                        aria-pressed={isSelected}
                                                    >
                                                        <div className="transformationResultArtCardMedia">
                                                            <div
                                                                className="transformationResultArtCardShimmer"
                                                                aria-hidden={isLoaded}
                                                            >
                                                                <span className="transformationResultArtCardShimmerBar" />
                                                            </div>
                                                            <img
                                                                src={url}
                                                                alt={artDisplayName}
                                                                className={isLoaded ? "is-visible" : ""}
                                                                draggable={false}
                                                            />
                                                        </div>
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div
                                    className={`transformationResultArtCard${resultImageLoaded ? " is-loaded" : " is-shimmer"}`}
                                >
                                    <div className="transformationResultArtCardMedia">
                                        <div
                                            className="transformationResultArtCardShimmer"
                                            aria-hidden={resultImageLoaded}
                                        >
                                            <span className="transformationResultArtCardShimmerBar" />
                                        </div>
                                        <img
                                            ref={resultImageRef}
                                            src={resultImage}
                                            alt={styleSlug}
                                            className={resultImageLoaded ? "is-visible" : ""}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>


                        <div
                            className={`transformationResultButtons${showActionButtons ? "" : " is-hidden"}${isComparePhase ? " is-single" : ""}`}
                            >
                            {!isComparePhase && (
                                <p className="transformationResultHeading mb-4 mt-3 px-4">
                                    {resultHeadings[resultPhase]}
                                </p>
                            )}
                            <button
                                type="button"
                                className="btnResultYes"
                                onClick={handleYes}
                                disabled={isSavingToFavourites || isUpdatingOrder}
                            >
                                Yes
                            </button>

                            {isSaveArtworkPhase && (
                                <button
                                    type="button"
                                    className="btnResultNo"
                                    onClick={handleNo}
                                    disabled={isSavingToFavourites || isUpdatingOrder}
                                >
                                    No
                                </button>
                            )}

                             {showTryAgain && (
                            <button type="button" className="transformationResultTryAgain" onClick={handleTryAgain}>
                                Generate one more
                            </button>
                        )}
                        </div>

                       
                    </div>
                </div>
            </StationThemeLayout>

            {showBackConfirmModal && (
                <SignOutConfirmModal
                    show={showBackConfirmModal}
                    onHide={() => setShowBackConfirmModal(false)}
                    onConfirm={() => {
                        setShowBackConfirmModal(false);
                        navigate("/");
                    }}
                    message="Are you sure you want to go back to the home screen?"
                    cancelLabel="No"
                    confirmLabel="Yes"
                />
            )}
            {showCompareConfirmModal && pendingCompareSelection && (
                <SelectHobbyConfirmModal
                    show={showCompareConfirmModal}
                    onHide={handleCompareConfirmNo}
                    hobbyName={pendingCompareSelection.label}
                    hobbyImageUrl={pendingCompareSelection.url}
                    fallbackImage={pendingCompareSelection.url}
                    isLandscapeArt={isArtLandscape}
                    onConfirm={handleCompareConfirmYes}
                />
            )}

            {signinModal && (
                <SigninModal
                    show={signinModal}
                    onHide={() => {
                        setSigninModal(false);
                    }}
                    setSignupModal={setSignupModal}
                    setForgotPasswordModal={setForgotPasswordModal}
                    setSigninModal={setSigninModal}
                    onAuthSuccess={handleAuthSuccess}
                    onGoogleAuth={handleGoogleAuth}
                />
            )}
            {forgotPasswordModal && (
                <ForgotPasswordModal
                    show={forgotPasswordModal}
                    onHide={() => setForgotPasswordModal(false)}
                    setForgotPasswordModal={setForgotPasswordModal}
                    setSigninModal={setSigninModal}
                    setSignupModal={setSignupModal}
                    setVerificationModal={setVerificationModal}
                />
            )}
            {verificationModal && (
                <VerificationModal
                    show={verificationModal}
                    onHide={() => setVerificationModal(false)}
                    setResetModal={setResetModal}
                    setVerificationModal={setVerificationModal}
                />
            )}
            {resetModal && (
                <ResetModal
                    show={resetModal}
                    onHide={() => setResetModal(false)}
                    setResetModal={setResetModal}
                    setSuccessModal={setSuccessModal}
                />
            )}
            {successModal && (
                <SuccessModal
                    show={successModal}
                    onHide={() => setSuccessModal(false)}
                    setSuccessModal={setSuccessModal}
                    setSigninModal={setSigninModal}
                />
            )}

            {signupModal && (
                <SignupModal
                    show={signupModal}
                    onHide={() => setSignupModal(false)}
                    setSignupModal={setSignupModal}
                    setSigninModal={setSigninModal}
                    setEmailVerificationModal={setEmailVerificationModal}
                    onAuthSuccess={handleAuthSuccess}
                    onGoogleAuth={handleGoogleAuth}
                />
            )}
            {emailVerificationModal && (
                <EmailVerificationModal
                    show={emailVerificationModal}
                    onHide={() => setSignupModal(false)}
                    setSaveModal={setSaveModal}
                />
            )}

            {(loading || success) &&
                <LoaderSaveModal
                    show={loading || success}
                    loading={loading}
                    success={success}
                    onHide={() => {
                        setLoading(false);
                        setSuccess(false);
                    }}
                />
            }
        </>
    );
};

export default TransformationResultScreen;
